import {
	AfterViewInit,
	Component,
	ElementRef,
	forwardRef,
	inject,
	input,
	OnDestroy,
	OnInit,
	output,
	Renderer2,
	ViewChild
} from "@angular/core";
import { ControlValueAccessor, FormBuilder, FormControl, FormGroup, NG_VALUE_ACCESSOR } from "@angular/forms";
import { MatLegacyAutocompleteTrigger as MatAutocompleteTrigger } from "@angular/material/legacy-autocomplete";
import { TransactionOption, TransactionOptionsGroup } from "@shared/models/select-option.interface";
import { debounceTime, distinctUntilChanged, map, startWith, Subject, takeUntil } from "rxjs";
import { InputAutocompleteService } from "../input-autocomplete/_services/input-autocomplete.service";

@Component({
	selector: "app-multi-layer-status-filter",
	templateUrl: "./multi-layer-status-filter.component.html",
	styleUrl: "./multi-layer-status-filter.component.scss",
	providers: [
		{
			provide: NG_VALUE_ACCESSOR,
			useExisting: forwardRef(() => MultiLayerStatusFilterComponent),
			multi: true
		}
	]
})
export class MultiLayerStatusFilterComponent implements ControlValueAccessor, OnInit, AfterViewInit, OnDestroy {
	/**INPUTS / VARIABLES TO USE */
	transactionOptions = input<TransactionOptionsGroup[]>([]);
	label = input<string>("");

	control: FormControl = new FormControl();
	optionSearchForm!: FormGroup;

	filteredTransactionOptions: TransactionOptionsGroup[] = [];
	selectedTransactionOptions: TransactionOption[] = [];
	selectedStatuses: string[] = [];
	noBulkSelected: TransactionOption = {
		text: "",
		value: "",
		group: "",
		selected: false,
		disable: false,
		statusesLinked: [],
		buttonText: ""
	};

	filterOpen = false;
	hidePlacehoder = false;

	destroy$: Subject<void> = new Subject();

	onChange: any = () => {};
	onTouched: any = () => {};

	/** DOM SELECTORS */
	@ViewChild("optionContainer") optionContainer!: ElementRef;
	@ViewChild("input") input!: ElementRef<HTMLInputElement>;
	@ViewChild("scrollContainer") scrollContainerRef!: ElementRef;
	@ViewChild("searchInput") searchInput!: ElementRef;
	@ViewChild(MatAutocompleteTrigger) autocomplete!: MatAutocompleteTrigger;

	/** OUTPUTS */
	emitScrollerReachedTheBottom = output<boolean>();
	filterStatusSelectionChange = output<string[]>();
	bulkActionSelected = output<TransactionOption>();

	/** IMPORT DECLARATIONS */
	public inputAutocompleteService = inject(InputAutocompleteService);
	private formBuilder = inject(FormBuilder);
	private renderer = inject(Renderer2);

	ngOnInit(): void {
		this.initForm();
		this.resetStatusFilter(true);
	}

	ngAfterViewInit(): void {
		this.attachScrollListener();
		this.updateOptionSelectedToDisplay();
	}

	initForm(): void {
		this.optionSearchForm = this.formBuilder.group({
			search: [""]
		});

		this.optionSearchForm
			.get("search")
			?.valueChanges.pipe(
				takeUntil(this.destroy$),
				debounceTime(500),
				distinctUntilChanged(),
				startWith(""),
				map((textInput: string) => {
					if (textInput) {
						return this.filterTransactionOptions(this.transactionOptions(), textInput);
					} else {
						return this.transactionOptions();
					}
				})
			)
			.subscribe(options => {
				if (options && options.length > 0) {
					this.filteredTransactionOptions = options;
				} else {
					this.filteredTransactionOptions = [
						{
							type: "noOptions",
							name: "",
							options: [
								{
									text: "No items found",
									value: "noOptions",
									group: "noOption",
									selected: false,
									disable: false
								}
							]
						}
					];
				}
			});
	}

	/**
	 * Check/unchecked status/bulk options when selected
	 * @param event: event value
	 * @param option: option value
	 */
	optionChecked(event: Event, option: TransactionOption): void {
		event.stopPropagation();
		if (option.value !== "noOptions") {
			this.toggleStatus(option);
		}
		this.setFocusOnSearch();
	}

	/**
	 * Toggle status/bulk options
	 * @param option: option value
	 */
	toggleStatus(option: TransactionOption): void {
		if (option.group === "statusOption") {
			if (option.value !== "statuses") {
				option.selected = !option.selected;

				this.filteredTransactionOptions[0].options.forEach(status => {
					if (status.value === "statuses") {
						status.selected &&= false;
					}
				});
			} else {
				const allSelected = !option.selected;
				this.filteredTransactionOptions[0].options.forEach(status => {
					status.selected = allSelected;
				});
			}
		} else {
			this.resetStatusFilter();
			const bulkActionSelected = !option.selected;

			if (bulkActionSelected) {
				this.filteredTransactionOptions.forEach(opt => {
					opt.options.forEach(status => {
						if (status.value !== option.value && !option.statusesLinked?.includes(status.value)) {
							status.disable = bulkActionSelected;
						} else {
							status.disable = !bulkActionSelected;
							status.selected = bulkActionSelected;
						}
					});
				});
			} else {
				this.filteredTransactionOptions.forEach(opt => {
					opt.options.forEach(status => {
						status.selected = false;
						status.disable = false;
					});
				});
			}
		}
		this.updateSelectedTransactionOptions();
	}

	/**
	 * Initialized selected/disable value to false
	 * of all status options when bulk action is selected.
	 */
	resetStatusFilter(isInitial = false): void {
		if (!isInitial) {
			this.filteredTransactionOptions[0].options.forEach(opt => {
				opt.selected = false;
				opt.disable = false;
			});
		} else {
			this.filteredTransactionOptions.forEach(opt => {
				opt.options.forEach(stat => {
					stat.selected = false;
					stat.disable = false;
				});
			});

			this.updateSelectedTransactionOptions();
			this.filterStatusSelectionChange.emit(
				history?.state?.payoutStatusFilter?.length ? history.state.payoutStatusFilter : this.selectedStatuses
			);
		}
	}

	/**
	 * Update selected status/bulk options
	 */
	updateSelectedTransactionOptions(): void {
		const transactionOptions = this.filteredTransactionOptions;
		const isBulkSelected = this.filteredTransactionOptions
			.find(grp => grp.type === "bulk")
			?.options.some(status => status.selected);

		let statusSelected: string[] = [];

		if (isBulkSelected) {
			this.selectedTransactionOptions = transactionOptions
				.find(grp => grp.type === "bulk")
				?.options.filter(status => status.selected)!;

			transactionOptions
				.find(grp => grp.type === "status")
				?.options.forEach(status => {
					if (status.selected) {
						statusSelected.push(status.value);
					}
				});

			this.bulkActionSelected.emit(this.selectedTransactionOptions[0]);
		} else {
			this.selectedTransactionOptions = transactionOptions
				.find(grp => grp.type === "status")
				?.options.filter(status => status.selected && status.value !== "statuses")!;

			transactionOptions
				.find(grp => grp.type === "status")
				?.options.forEach(status => {
					if (status.selected) {
						statusSelected.push(status.value);
					}
				});

			this.bulkActionSelected.emit(this.noBulkSelected);
		}

		this.selectedStatuses = statusSelected;
		this.hidePlacehoder = this.selectedTransactionOptions.length > 0;
		this.updateOptionSelectedToDisplay();
	}

	/**
	 * Open options dropdown
	 */
	openFilterDropdown(): void {
		this.control.setErrors(null);
		this.autocomplete.openPanel();
		this.filterOpen = !this.filterOpen;
		this.filterOpen ? this.setFocusOnSearch() : this.closeFilterDropdown(true);
	}

	/**
	 * Close options dropdown
	 * @param emit: boolean value
	 */
	closeFilterDropdown(emit: boolean): void {
		this.filterOpen = !emit;
		this.optionSearchForm.get("search")?.patchValue("", { emitEvent: true });
		this.filterStatusSelectionChange.emit(this.selectedStatuses);
		if (this.optionContainer) {
			this.optionContainer.nativeElement.blur();
		}
	}

	/**
	 * Filter out status/bulk options in search
	 * @param options: options value
	 * @param textInput: user search text value
	 * @returns: filtered options to show in dropdown
	 */
	filterTransactionOptions(options: TransactionOptionsGroup[], textInput: string): TransactionOptionsGroup[] {
		return options
			.map(group => ({
				...group,
				options: group.options.filter(opt => opt.text.toLowerCase().includes(textInput.toLowerCase()))
			}))
			.filter(type => type.options.length > 0);
	}

	/**
	 * Focus key on search when filter dropdown is open
	 */
	setFocusOnSearch(): void {
		setTimeout(() => {
			if (this.searchInput) {
				this.searchInput.nativeElement.focus();
			}
		}, 100);
	}

	/** FOR VALUE ACCESSOR */
	writeValue(value: any): void {}

	registerOnChange(fn: any): void {
		this.onChange = fn;
	}

	registerOnTouched(fn: any): void {
		this.onTouched = fn;
	}

	attachScrollListener(): void {
		const scrollContainer = this.scrollContainerRef?.nativeElement;
		if (scrollContainer) {
			scrollContainer.addEventListener("scroll", this.onScroll.bind(this));
		}
	}

	onScroll(): void {
		const scrollContainer = this.scrollContainerRef?.nativeElement;
		if (
			scrollContainer &&
			scrollContainer.scrollHeight - scrollContainer.scrollTop === scrollContainer.clientHeight
		) {
			this.emitScrollerReachedTheBottom.emit(true);
		}
	}

	/**
	 * Clear selected option values
	 * @param $event: event value
	 */
	clearSelected($event: Event): void {
		$event.stopPropagation();

		this.filteredTransactionOptions.forEach(grp => {
			grp.options.forEach(status => {
				status.selected = false;
				status.disable = false;
			});
		});
		this.hidePlacehoder = false;
		this.updateSelectedTransactionOptions();
		this.updateOptionSelectedToDisplay();
		this.closeFilterDropdown(true);
		this.clearOptionContainer();
	}

	handleEmptyInput($event: any): void {
		if (
			$event.target.value === "" ||
			(Array.isArray($event.target.value.length) && $event.target.value.length === 0)
		) {
			this.onChange(null);
		}
	}

	/**
	 * Update/initialize filter field placeholder
	 */
	updateOptionSelectedToDisplay(): void {
		if (this.optionContainer) {
			const container = this.optionContainer.nativeElement;

			while (container.firstChild) {
				container.removeChild(container.firstChild);
			}

			const maxOptionToDisplay = this.setMaxOptionsToDisplay();

			this.selectedTransactionOptions.forEach((val, i) => {
				i++;
				if (i <= maxOptionToDisplay) {
					if (val.value !== "statuses") {
						this.addOptionToContainer(container, val.text, false);
					}
				}
			});

			if (this.selectedTransactionOptions.length > maxOptionToDisplay) {
				this.addCounterToContainer(container, maxOptionToDisplay, false);
			}
		}
	}

	/**
	 * Create span for selected option
	 * @param container: container value
	 * @param optionText: option text value
	 * @param removeAfterwards: boolean value
	 * @returns: width of the option container created
	 */
	addOptionToContainer(container: ElementRef, optionText: string, removeAfterwards: boolean): number {
		const optionElement = this.renderer.createElement("span");

		if (removeAfterwards) this.renderer.setStyle(optionElement, "position", "absolute");
		this.renderer.setAttribute(optionElement, "data-test", optionText);
		this.renderer.addClass(optionElement, "input-option");
		this.renderer.setProperty(optionElement, "textContent", optionText);

		this.renderer.appendChild(container, optionElement);

		const offsetWidth: number = optionElement.offsetWidth;

		if (removeAfterwards) this.renderer.removeChild(container, optionElement);

		return offsetWidth;
	}

	/**
	 * Sets maximum option selected to display
	 * @returns: number of option selected to display
	 */
	setMaxOptionsToDisplay(): number {
		const container = this.optionContainer.nativeElement;
		if (container) {
			const containerWidth = container.clientWidth;
			const selectedOptions = this.selectedTransactionOptions;
			let totalOptionsWidth = 0;
			let maxOptionsToDisplay = 0;

			selectedOptions.forEach((opt, i) => {
				const optionWidth: number = this.addOptionToContainer(container, opt.text, true);
				totalOptionsWidth += optionWidth;

				if (i >= maxOptionsToDisplay) {
					const counterWidth = this.addCounterToContainer(container, maxOptionsToDisplay, true);
					totalOptionsWidth += counterWidth;
				}

				if (totalOptionsWidth <= containerWidth) {
					maxOptionsToDisplay++;
				}
			});

			return maxOptionsToDisplay;
		}
		return 0;
	}

	/**
	 * Add span to show number of option selected when reached
	 * max number of selected option to display
	 * @param container: container value
	 * @param maxOptionsToDisplay: max number of option to display
	 * @param removeAfterwards: boolean value
	 * @returns: width of counter container
	 */
	addCounterToContainer(container: ElementRef, maxOptionsToDisplay: number, removeAfterwards: boolean): number {
		const counterElement = this.renderer.createElement("span");

		if (removeAfterwards) this.renderer.setStyle(counterElement, "position", "absolute");
		this.renderer.addClass(counterElement, "counter");
		this.renderer.setProperty(
			counterElement,
			"textContent",
			`+ ${this.selectedTransactionOptions.length - maxOptionsToDisplay}`
		);

		this.renderer.appendChild(container, counterElement);

		const offsetWidth: number = counterElement.offsetWidth;

		if (removeAfterwards) this.renderer.removeChild(container, counterElement);

		return offsetWidth;
	}

	clearOptionContainer(): void {
		if (this.optionContainer) {
			const container = this.optionContainer.nativeElement;

			while (container.firstChild) {
				container.removeChild(container.firstChild);
			}
		}
	}

	ngOnDestroy(): void {
		this.destroy$.next();
		this.destroy$.complete();
	}
}
